import { getGetMyCurrentBalanceQueryKey } from '@/api/balance/balance';
import { useGetBookmarkStatus, useRemoveBookmark, useToggleBookmark } from '@/api/bookmarks/bookmarks';
import { getGetBookmarkStatusQueryKey } from '@/api/bookmarks/bookmarks';
import type { VoucherCodeResponse } from '@/api/model/voucherCodeResponse';
import type { VoucherResponse } from '@/api/model/voucherResponse';
import { useActivateVoucher } from '@/api/vouchers/vouchers';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DateTimeDisplay } from '@/components/ui/datetime-display';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { VoucherCategoryIcon } from './VoucherCategoryIcon';
import { cn } from '@/lib/utils';
import { getApiErrorMessage } from '@/lib/api-error';
import { useQueryClient } from '@tanstack/react-query';
import { Bookmark, MapPin } from 'lucide-react';
import React, { useState } from 'react';
import { toast } from 'sonner';
import { VoucherSuccessStage } from './VoucherSuccessStage';

type ModalStage = 'details' | 'success';

interface VoucherDetailsModalProps {
  voucher: VoucherResponse | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const VoucherDetailsModal: React.FC<VoucherDetailsModalProps> = ({ voucher, open, onOpenChange }) => {
  const [stage, setStage] = useState<ModalStage>('details');
  const [activatedCode, setActivatedCode] = useState<VoucherCodeResponse | null>(null);

  const activateVoucherMutation = useActivateVoucher();
  const queryClient = useQueryClient();

  // Get bookmark status
  const { data: bookmarkStatus } = useGetBookmarkStatus(voucher?.id ?? '', {
    query: {
      enabled: !!voucher,
    },
  });

  const isBookmarked = bookmarkStatus?.is_bookmarked ?? false;

  const toggleBookmarkMutation = useToggleBookmark({
    mutation: {
      onSuccess: data => {
        if (voucher) {
          // Update only the specific voucher's bookmark status in the cache
          queryClient.setQueryData(getGetBookmarkStatusQueryKey(voucher.id), data);
          // Invalidate the bookmarked vouchers list to refresh the list view
          queryClient.invalidateQueries({
            queryKey: ['/api/bookmarks/vouchers'],
          });
        }
      },
    },
  });

  const removeBookmarkMutation = useRemoveBookmark();

  const handleBookmarkClick = async () => {
    if (voucher) {
      try {
        // Only toggle bookmark for this specific voucher
        await toggleBookmarkMutation.mutateAsync({ voucherId: voucher.id });
      } catch (error) {
        toast.error(getApiErrorMessage(error, 'Fehler beim Aktualisieren der Merkliste'));
      }
    }
  };

  const handleClose = () => {
    setStage('details');
    setActivatedCode(null);
    onOpenChange(false);
  };

  const handleRedeemVoucher = async () => {
    if (!voucher) return;

    try {
      const code = await activateVoucherMutation.mutateAsync({
        voucherId: voucher.id,
      });

      // Invalidate balance query to refresh user's balance
      await queryClient.invalidateQueries({
        queryKey: getGetMyCurrentBalanceQueryKey(),
      });

      // If the voucher was bookmarked, remove it from bookmarks
      if (isBookmarked) {
        try {
          await removeBookmarkMutation.mutateAsync({
            voucherId: voucher.id,
          });
          // Update cache after successful removal
          if (voucher && bookmarkStatus) {
            queryClient.setQueryData(getGetBookmarkStatusQueryKey(voucher.id), {
              ...bookmarkStatus,
              is_bookmarked: false,
            });
          }
          queryClient.invalidateQueries({
            queryKey: ['/api/bookmarks/vouchers'],
          });
        } catch (_error) {
          // Don't block the activation flow
        }
      }

      setActivatedCode(code);
      setStage('success');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Aktivieren des Gutscheins fehlgeschlagen'));
    }
  };

  if (!voucher) return null;

  const isLoading = activateVoucherMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className='bg-white rounded-2xl p-0 max-w-4xl w-full max-h-[95vh] lg:w-[896px] overflow-hidden border-0 shadow-[0px_6px_20px_0px_rgba(89,225,255,0.1)]'
        showCloseButton={true}
      >
        {stage === 'details' && (
          <div className='flex flex-col lg:flex-row h-full'>
            {/* Left column - Image */}
            <div className='relative w-full lg:w-[448px] h-64 lg:h-full'>
              <div
                className='w-full h-full bg-cover bg-center rounded-t-2xl lg:rounded-l-2xl lg:rounded-tr-none'
                style={{
                  backgroundImage: voucher.images?.[0] ? `url(${voucher.images[0]})` : `url('/placeholder.png')`,
                }}
              ></div>
              <div className='absolute top-4 left-4 flex h-10 w-10 items-center justify-center text-swd-tuerkis rounded-full bg-white shadow-md lg:top-6 lg:left-6'>
                <VoucherCategoryIcon category={voucher.provider.category} />
              </div>
            </div>

            {/* Right column - Details */}
            <div className='flex-1 p-4 lg:p-8 flex flex-col justify-start lg:justify-center gap-6 lg:gap-10 max-w-none overflow-y-auto lg:overflow-y-visible'>
              {/* Header section */}
              <div className='flex flex-col gap-4'>
                <div className='flex flex-col gap-6'>
                  <div className='flex flex-col gap-4'>
                    {/* Title and location */}
                    <div className='flex flex-col gap-2'>
                      <div className='flex items-start justify-between gap-3 pr-10 lg:pr-12'>
                        <h1 className='text-2xl lg:text-[28px] font-semibold text-[#00535f] leading-8 lg:leading-9'>
                          {voucher.title}
                        </h1>
                        <Button
                          variant='ghost'
                          size='icon'
                          className='p-0 size-6'
                          onClick={handleBookmarkClick}
                          disabled={toggleBookmarkMutation.isPending}
                          aria-label={isBookmarked ? 'Von Merkliste entfernen' : 'Gutschein merken'}
                        >
                          <Bookmark
                            className={cn(
                              'size-6 transition-colors',
                              toggleBookmarkMutation.isPending ? 'text-muted-foreground' : 'text-swd-orange'
                            )}
                            fill={isBookmarked ? 'currentColor' : 'none'}
                          />
                        </Button>
                      </div>
                      <div className='flex flex-col gap-1'>
                        <div className='text-base lg:text-[18px] font-semibold text-muted-foreground'>
                          {voucher.provider.name}
                        </div>
                        <div className='flex items-center gap-2 text-muted-foreground'>
                          <MapPin className='w-3 h-4 flex-shrink-0' />
                          <span className='text-sm lg:text-[16px] font-medium tracking-[0.64px] leading-5 lg:leading-6'>
                            {voucher.provider.address}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Tags */}
                    <div className='flex flex-wrap gap-2 lg:gap-3'>
                      <Badge className='bg-[#d3ed2e] text-[#00535f] text-xs lg:text-[14px] font-bold tracking-[0.56px] leading-4 lg:leading-5 px-2 lg:px-3 py-1 lg:py-1.5 rounded-lg border-0'>
                        {voucher.provider.category}
                      </Badge>
                    </div>
                  </div>

                  {/* Description section */}
                  <div className='flex flex-col gap-2'>
                    <h3 className='text-base lg:text-[18px] font-semibold text-muted-foreground leading-5 lg:leading-6'>
                      Angebotsbeschreibung
                    </h3>
                    <p className='text-sm lg:text-[16px] font-medium text-[#72727a] tracking-[0.64px] leading-5 lg:leading-6'>
                      {voucher.description ||
                        `Genießen Sie ${voucher.title} mit ${voucher.value}% Rabatt auf Ihre gesamte Rechnung. Gültig für alle Speisen und Getränke. Nicht kombinierbar mit anderen Aktionen.`}
                    </p>

                    {/* Valid until and minimum order */}
                    <div className='flex flex-col sm:flex-row gap-4 mt-2'>
                      <div>
                        <span className='text-xs lg:text-[14px] font-medium text-zinc-400 tracking-[0.56px] leading-4 lg:leading-5'>
                          Gültig bis:
                        </span>
                        <div className='text-xs lg:text-[14px] font-bold text-muted-foreground tracking-[0.56px] leading-4 lg:leading-5'>
                          <DateTimeDisplay date={voucher.valid_until} format='date-only' locale='de-DE' />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Terms section */}
                <div className='flex flex-col gap-2'>
                  <h3 className='text-base lg:text-[18px] font-semibold text-muted-foreground leading-5 lg:leading-6'>
                    Einlösebedingungen
                  </h3>
                  <div className='flex flex-col gap-2 text-xs lg:text-[14px] font-medium text-[#72727a] tracking-[0.56px] leading-4 lg:leading-5'>
                    {voucher.conditions ? (
                      <div className='whitespace-pre-line'>{voucher.conditions}</div>
                    ) : (
                      <>
                        <div>• Pro Person nur ein Gutschein einlösbar</div>
                        <div>• Nicht mit anderen Aktionen kombinierbar</div>
                        <div>• Keine Barauszahlung möglich</div>
                        <div>• Reservierung empfohlen</div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Action buttons */}
              <div className='flex flex-col sm:flex-row items-stretch sm:items-center gap-3 lg:gap-4'>
                <Button
                  onClick={handleRedeemVoucher}
                  disabled={isLoading}
                  className='bg-muted-foreground hover:bg-muted-foreground/90 text-white font-semibold text-base lg:text-[18px] px-8 lg:px-12 py-4 lg:py-5 rounded-2xl lg:rounded-[20px] leading-5 lg:leading-6 flex-1 sm:flex-none'
                >
                  {isLoading ? 'Wird aktiviert...' : 'Gutschein aktivieren'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {stage === 'success' && activatedCode && (
          <div className='max-h-[95vh] overflow-y-auto overscroll-contain'>
            <VoucherSuccessStage code={activatedCode} onClose={handleClose} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
